import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";

import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { GUIDES } from "@/content/guides";

const GUIDES_FAQS = [
  {
    q: "What makes a guide different from a blog post?",
    a: "Depth and shelf life. A guide is a complete operating manual for a discipline — structured in chapters, revised as the discipline changes, and meant to be referenced repeatedly. A blog post is a single observation with a shorter useful life.",
  },
  {
    q: "How long are these guides?",
    a: "Each pillar guide runs long enough to cover a discipline end to end — typically a 15 to 30 minute read — because compressing a full method into 800 words produces guidance too shallow to act on safely.",
  },
  {
    q: "Do I need to read a guide start to finish?",
    a: "No. Each guide opens with a TL;DR and chapter structure so you can jump to the section relevant to where you are — audit, strategy, or execution — without reading the whole thing.",
  },
  {
    q: "How often are the guides updated?",
    a: "We revise a guide whenever the underlying mechanics change materially — a Core Web Vitals metric swap, a shift in how AI Overviews source citations, a change to Google's local ranking factors. The updated date on each guide reflects the last substantive revision, not a cosmetic edit.",
  },
  {
    q: `Are the guides specific to ${siteConfig.contact.address.city} or ${siteConfig.contact.address.state} businesses?`,
    a: `The mechanics are universal; the local SEO guide includes ${siteConfig.contact.address.city}- and ${siteConfig.contact.address.state}-specific examples because that's where most of our client base sits, but the method applies to any market.`,
  },

  {
    q: "Can I follow a guide without hiring an agency?",
    a: "Yes. The guides are written to be genuinely actionable by an in-house team. Some readers use them as a DIY roadmap; others use them to brief and evaluate an agency, including us.",
  },
  {
    q: "Which guide should I start with if I've never done SEO before?",
    a: "Start with the Complete Guide to SEO in 2026 — it's the foundation the other guides assume. Then branch into AEO/GEO, local, or technical depending on which constraint is actually limiting your traffic.",
  },
];

export const Route = createFileRoute("/guides/")({
  head: () => ({ ...pageMeta({
    path: "/guides/",
    title: `SEO Pillar Guides 2026 — Expert ${siteConfig.contact.address.state} SEO Knowledge | ${siteConfig.name}`,
    description:
      `Long-form operating manuals for modern search from ${siteConfig.name}: the Complete Guide to SEO in 2026, the AEO/GEO Playbook, the Local SEO Playbook, and the Technical SEO Audit Checklist.`,
    keywords: [
      "SEO guide 2026",
      "AEO playbook",
      "GEO playbook",
      "local SEO guide",
      "technical SEO checklist",
      "SEO tutorial",
      "complete SEO guide",
      `SEO company ${siteConfig.contact.address.city}`,
      `${siteConfig.contact.address.state} SEO experts`,

    ],
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides/" }]),
    faqLd(GUIDES_FAQS),
  ) }),
  component: GuidesIndex,
});

function GuidesIndex() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-24 pb-12">
          <EyebrowLabel>Pillar guides</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Operating manuals.
            <br />
            <span className="text-primary">Not blog posts.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            Each guide is structured the way we structure engagements — TL;DR up top, chapters
            that map to milestones, and a final recommendation you can actually plan against.
          </p>
        </section>

        <section className="container-page pb-32 grid gap-10 lg:grid-cols-2">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to="/guides/$slug/"
              params={{ slug: g.slug }}
              className="group rounded-3xl border border-border p-8 md:p-10 bg-card/40 hover:bg-card/60 hover:border-primary/40 transition-all"
            >
              <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
                <span>{g.readingMinutes} min</span>
                <span>·</span>
                <span>Updated {new Date(g.updatedISO).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">{g.title}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{g.dek}</p>
              <div className="mt-6 rounded-xl border border-border/70 bg-background/40 p-5">
                <div className="text-xs uppercase tracking-widest text-primary">TL;DR</div>
                <ul className="mt-2 space-y-1.5 text-sm text-foreground/85">
                  {g.tldr.slice(0, 3).map((t) => (
                    <li key={t} className="flex gap-2"><span className="text-primary">·</span>{t}</li>
                  ))}
                </ul>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-sm text-primary group-hover:gap-3 transition-all">
                Read the guide <span aria-hidden>→</span>
              </span>
            </Link>
          ))}
        </section>
        <section className="container-page pb-20 border-t border-border pt-16">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <EyebrowLabel>What this collection covers</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                Full operating manuals, built to survive more than one algorithm update.
              </h2>
              <div className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
                <p>
                  Most "ultimate guides" online are blog posts wearing a longer title. Ours are
                  structured differently: each one covers a full discipline — general SEO,
                  answer-engine and generative-engine optimisation, local SEO, or technical
                  auditing — from first principles through to a prioritised execution plan you
                  can hand to a developer or run yourself.
                </p>
                <p>
                  They're written for people who need to make a decision with budget attached:
                  a marketing lead scoping next year's programme, a founder deciding whether to
                  hire in-house or retain an agency, or a technical stakeholder who needs to
                  understand why an SEO recommendation touches their infrastructure.
                </p>
                <p>
                  Because a pillar guide represents dozens of hours of consolidated practitioner
                  time, we keep the library deliberately small rather than publishing a guide
                  for every long-tail keyword. Four durable guides, kept current, beat forty
                  guides that go stale within a quarter.
                </p>
              </div>
            </div>
            <div>
              <EyebrowLabel>Editorial standards</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                How we research and structure a guide
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  ["Chapter parity with the work", "Each chapter maps to a phase we actually run on client engagements — audit, prioritisation, execution, measurement — so the structure isn't invented for readability alone."],
                  ["TL;DR before the argument", "Every guide opens with the conclusion and the top actions, then earns that conclusion in the chapters that follow, so a skim-reader still leaves informed."],
                  ["Benchmarks sourced from live accounts", "Where a guide states a target metric or timeline, it reflects ranges we've observed across client engagements, not industry-average folklore."],
                  ["Revision-tracked", "Guides carry a visible 'updated' date and are re-audited on a fixed schedule against current SERP behaviour, not left to drift for years."],
                  ["No filler chapters", "If a topic doesn't materially change the outcome of the discipline, it doesn't get a chapter — length is a consequence of coverage, not a target word count."],
                ].map(([h, b]) => (
                  <li key={h} className="rounded-xl border border-border/70 bg-background/40 p-5">
                    <div className="font-display text-base tracking-tight">{h}</div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{b}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="container-page pb-20">
          <EyebrowLabel>Recommended learning path</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            The order that matches how a real programme is built
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Each guide stands alone, but the four together form a sequence that mirrors how we
            actually scope a new engagement:
          </p>
          <ol className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">1 — Foundation</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Complete Guide to SEO in 2026 sets the shared vocabulary and priority order for
                everything else — start here if you're new to the discipline.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">2 — Diagnose</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Technical SEO Audit Checklist tells you whether your site can even be crawled,
                rendered, and indexed correctly before you invest further.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">3 — Reach</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                AEO/GEO Playbook extends the foundation into AI Overviews and generative answer
                engines, where the ranking rules diverge from classic SERPs.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">4 — Localise</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Local SEO Playbook applies all of the above to a market-specific business with a
                physical presence or service area.
              </p>
            </li>
          </ol>
        </section>

        <section className="container-page pb-20">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <EyebrowLabel>How this connects</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                Guides sit between the glossary and the services
              </h2>
              <p className="mt-4 text-foreground/85 leading-relaxed">
                If a guide chapter references a term you don't recognise, it links to the {" "}
                <Link to={"/glossary/" as any}   className="text-primary hover:underline">glossary</Link> definition
                rather than re-explaining it inline. If a chapter describes a deliverable we
                run for clients — a technical migration, a citation build, a content programme
                — it links through to the matching <Link to={"/services/" as any}   className="text-primary hover:underline">service page</Link> where
                that work is scoped and priced. The <Link to={"/blog/" as any}   className="text-primary hover:underline">blog</Link> then
                supplies the shorter-lived, field-tested updates that sit underneath each
                guide's more durable framework.
              </p>
            </div>
            <div>
              <EyebrowLabel>Common misconceptions</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                What readers get wrong about pillar guides
              </h2>
              <ul className="mt-6 space-y-3">
                {[
                  "“Longer means better for ranking.” Length is a side effect of covering a discipline properly, not a ranking lever on its own — a short guide covering a narrow topic well will outperform a padded long one.",
                  "“A guide replaces the need for judgement on your specific site.” Guides describe the general method; your site's history, platform, and market still require situational judgement a static document can't fully anticipate.",
                  "“AEO/GEO guidance is separate from real SEO.” Answer-engine and generative-engine optimisation build directly on classic technical and content fundamentals — they're an extension, not a replacement.",
                  "“Once you've read a guide, the work is done.” A guide describes the method; the compounding value comes from applying it consistently over months, not from having read it once.",
                ].map((t) => (
                  <li key={t} className="flex gap-3 text-sm text-foreground/85 leading-relaxed">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="container-page pb-32">
          <EyebrowLabel>FAQ</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Questions readers ask about the guides
          </h2>
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            {GUIDES_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-sm text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
